import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';

const GUEST_FAQS = [
  {
    id: 1,
    question: 'كيف يمكنني حجز جلسة تصوير؟',
    answer:
      'يمكنك حجز جلسة تصوير من خلال الذهاب إلى صفحة "حجز جديد" واتباع الخطوات. ستحتاج إلى اختيار المدينة، نوع التصوير، التاريخ والوقت، والمصور المفضل.',
  },
  {
    id: 2,
    question: 'كيف يمكنني إلغاء حجز؟',
    answer:
      'يمكنك إلغاء الحجز من صفحة "حجوزاتي" عن طريق الضغط على زر "إلغاء الطلب" بجانب الحجز المطلوب.',
  },
  {
    id: 3,
    question: 'متى يتم خصم المبلغ من حسابي؟',
    answer:
      'يتم خصم المبلغ بعد تأكيد المصور للحجز. ستحصل على إشعار عند تأكيد الحجز وخصم المبلغ.',
  },
  {
    id: 4,
    question: 'ماذا أفعل إذا لم يصل المصور في الوقت المحدد؟',
    answer:
      'إذا لم يصل المصور في الوقت المحدد، يرجى التواصل معنا فوراً عبر البريد أو الهاتف أدناه، أو افتح تذكرة دعم بعد تسجيل الدخول.',
  },
];

const TICKET_CATEGORIES = [
  { key: "booking", label: "مشكلة في الحجز", icon: "Calendar", description: "استفسارات أو مشاكل متعلقة بالحجوزات" },
  { key: "payment", label: "مشكلة في الدفع", icon: "CreditCard", description: "مشاكل في عمليات الدفع أو الاسترداد" },
  { key: "technical", label: "مشكلة تقنية", icon: "Settings", description: "أخطاء في الموقع أو التطبيق" },
  { key: "account", label: "مشكلة في الحساب", icon: "User", description: "مشاكل في تسجيل الدخول أو الحساب" },
  { key: "photographer", label: "شكوى على مصور", icon: "Camera", description: "شكوى أو ملاحظة على مصور" },
  { key: "complaint", label: "شكوى عامة", icon: "AlertCircle", description: "شكوى عامة على الخدمة" },
  { key: "suggestion", label: "اقتراح", icon: "Lightbulb", description: "اقتراح لتحسين الخدمة" },
  { key: "other", label: "أخرى", icon: "MoreHorizontal", description: "استفسارات أخرى" },
];

const TICKET_STATUSES = {
  open: { label: "مفتوحة", color: "blue" },
  in_progress: { label: "قيد المعالجة", color: "amber" },
  waiting_user: { label: "بانتظار ردك", color: "purple" },
  waiting_admin: { label: "بانتظار الرد", color: "orange" },
  resolved: { label: "تم الحل", color: "green" },
  closed: { label: "مغلقة", color: "slate" },
};

export default function SupportPage() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestFaqOpen, setGuestFaqOpen] = useState(null);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReplies, setTicketReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // New ticket form
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicket, setNewTicket] = useState({
    category: '',
    subject: '',
    description: '',
    priority: 'medium',
  });
  const [creatingTicket, setCreatingTicket] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        if (user) {
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          setUserProfile(profile);
          
          // Load user's tickets
          await loadTickets(user.id);
        }
      } catch (error) {
        console.error('Error initializing:', error);
      } finally {
        setLoading(false);
      }
    };
    
    init();
  }, []);

  const loadTickets = async (userId) => {
    setTicketsLoading(true);
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setTickets(data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setTicketsLoading(false);
    }
  };

  const loadTicketReplies = async (ticketId) => {
    try {
      const { data, error } = await supabase
        .from('ticket_replies')
        .select(`
          *,
          user:user_id (
            full_name, role
          )
        `)
        .eq('ticket_id', ticketId)
        .eq('is_internal', false) // Don't show internal notes
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      setTicketReplies(data || []);
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  };

  const createTicket = async () => {
    if (!newTicket.category || !newTicket.subject || !newTicket.description) {
      alert('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    
    setCreatingTicket(true);
    try {
      const { data: profile } = await supabase
        .from('photographer_profiles')
        .select('user_id')
        .eq('user_id', user.id)
        .single();
      
      const userType = profile ? 'photographer' : 'customer';
      
      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          user_type: userType,
          category: newTicket.category,
          subject: newTicket.subject,
          description: newTicket.description,
          priority: newTicket.priority,
        })
        .select()
        .single();
      
      if (error) throw error;
      
      alert('تم إنشاء التذكرة بنجاح! ✅\nرقم التذكرة: ' + data.ticket_number);
      
      setNewTicket({ category: '', subject: '', description: '', priority: 'medium' });
      setShowNewTicketForm(false);
      await loadTickets(user.id);
      
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('فشل إنشاء التذكرة: ' + error.message);
    } finally {
      setCreatingTicket(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const { error } = await supabase
        .from('ticket_replies')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: user.id,
          message: replyText.trim(),
        });
      
      if (error) throw error;
      
      setReplyText('');
      await loadTicketReplies(selectedTicket.id);
      await loadTickets(user.id);
      
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('فشل إرسال الرد');
    } finally {
      setSendingReply(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await loadTicketReplies(ticket.id);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Icon name="Loader" size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4" dir="rtl">
        <div className="max-w-lg mx-auto">
          <div className="mb-6">
            <BackButton to="/account" label="حسابي" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Icon name="Headphones" size={28} className="text-primary" />
              </div>
              <div>
                <h1
                  className="text-2xl font-bold text-slate-900"
                  style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
                >
                  الدعم والمساعدة
                </h1>
                <p className="text-slate-600 text-sm mt-1">
                  أسئلة شائعة وطرق التواصل. لفتح تذكرة ومتابعتها، سجّل الدخول إلى حسابك.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/customer-login"
                className="flex-1 text-center px-4 py-3 bg-primary text-white rounded-xl font-bold"
              >
                تسجيل الدخول
              </Link>
              <Link
                to="/customer-registration"
                className="flex-1 text-center px-4 py-3 border-2 border-slate-200 text-slate-800 rounded-xl font-bold hover:bg-slate-50"
              >
                إنشاء حساب
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
            <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Icon name="HelpCircle" size={20} className="text-primary" />
              الأسئلة الشائعة
            </h2>
            <div className="space-y-2">
              {GUEST_FAQS.map((faq) => (
                <div key={faq.id} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setGuestFaqOpen(guestFaqOpen === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between gap-2 p-4 text-right hover:bg-slate-50"
                  >
                    <span className="font-semibold text-sm text-slate-800">{faq.question}</span>
                    <Icon
                      name={guestFaqOpen === faq.id ? 'ChevronUp' : 'ChevronDown'}
                      size={18}
                      className="text-slate-400 flex-shrink-0"
                    />
                  </button>
                  {guestFaqOpen === faq.id && (
                    <p className="text-sm text-slate-600 leading-relaxed px-4 pb-4 pt-0">{faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Icon name="Phone" size={20} className="text-primary" />
              تواصل معنا
            </h2>
            <div className="space-y-3 text-sm">
              <a
                href="https://wa.me/966571954344"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100"
                dir="ltr"
              >
                <Icon name="MessageCircle" size={18} className="text-primary flex-shrink-0" />
                <span className="font-medium text-slate-800">+966 57 195 4344</span>
              </a>
              <a
                href="mailto:info@mashhad.sa"
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100"
                dir="ltr"
              >
                <Icon name="Mail" size={18} className="text-primary flex-shrink-0" />
                <span className="font-medium text-slate-800">info@mashhad.sa</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Icon name="Headphones" size={28} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "IBM Plex Sans Arabic, sans-serif" }}>
                  الدعم الفني
                </h1>
                <p className="text-slate-600">مرحباً {userProfile?.full_name || 'بك'}، كيف يمكننا مساعدتك؟</p>
              </div>
            </div>
            <button
              onClick={() => setShowNewTicketForm(true)}
              className="px-6 py-3 bg-primary text-white rounded-xl font-bold flex items-center gap-2 hover:bg-primary-dark"
            >
              <Icon name="Plus" size={20} />
              تذكرة جديدة
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-slate-100 p-4">
              <h2 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
                <Icon name="Ticket" size={20} className="text-primary" />
                تذاكري ({tickets.length})
              </h2>
              
              {ticketsLoading ? (
                <div className="text-center py-8">
                  <Icon name="Loader" size={24} className="animate-spin text-primary mx-auto" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Inbox" size={40} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">لا توجد تذاكر</p>
                  <button
                    onClick={() => setShowNewTicketForm(true)}
                    className="mt-4 px-4 py-2 bg-primary/10 text-primary rounded-xl font-bold text-sm"
                  >
                    إنشاء تذكرة جديدة
                  </button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {tickets.map(ticket => {
                    const status = TICKET_STATUSES[ticket.status] || { label: ticket.status, color: 'slate' };
                    const category = TICKET_CATEGORIES.find(c => c.key === ticket.category);
                    
                    return (
                      <button
                        key={ticket.id}
                        onClick={() => openTicket(ticket)}
                        className={`w-full text-right p-4 rounded-xl transition-all ${
                          selectedTicket?.id === ticket.id
                            ? 'bg-primary/10 border-2 border-primary'
                            : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">{ticket.subject}</p>
                            <p className="text-xs text-slate-500 mt-1">{ticket.ticket_number}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                            status.color === 'green' ? 'bg-green-100 text-green-700' :
                            status.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                            status.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                            status.color === 'purple' ? 'bg-blue-100 text-blue-800' :
                            status.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {status.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          <Icon name={category?.icon || 'HelpCircle'} size={12} />
                          <span>{category?.label || ticket.category}</span>
                          <span>•</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Ticket Details */}
          <div className="lg:col-span-2">
            {selectedTicket ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                {/* Ticket Header */}
                <div className="flex items-start justify-between gap-4 mb-6 pb-4 border-b border-slate-100">
                  <div>
                    <h2 className="font-bold text-lg text-slate-900">{selectedTicket.subject}</h2>
                    <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                      <span className="font-mono bg-slate-100 px-2 py-1 rounded">{selectedTicket.ticket_number}</span>
                      <span>•</span>
                      <span>{TICKET_CATEGORIES.find(c => c.key === selectedTicket.category)?.label}</span>
                      <span>•</span>
                      <span>{new Date(selectedTicket.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                    TICKET_STATUSES[selectedTicket.status]?.color === 'green' ? 'bg-green-100 text-green-700' :
                    TICKET_STATUSES[selectedTicket.status]?.color === 'amber' ? 'bg-amber-100 text-amber-700' :
                    TICKET_STATUSES[selectedTicket.status]?.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                    TICKET_STATUSES[selectedTicket.status]?.color === 'purple' ? 'bg-blue-100 text-blue-800' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {TICKET_STATUSES[selectedTicket.status]?.label || selectedTicket.status}
                  </span>
                </div>

                {/* Conversation */}
                <div className="space-y-4 max-h-[400px] overflow-y-auto mb-4">
                  {/* Original Message */}
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">
                        {(userProfile?.full_name || 'أ')[0]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-bold">أنت</p>
                          <span className="text-xs text-slate-500">
                            {new Date(selectedTicket.created_at).toLocaleString('ar-SA')}
                          </span>
                        </div>
                        <p className="mt-2 text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Replies */}
                  {ticketReplies.map(reply => (
                    <div 
                      key={reply.id}
                      className={`rounded-xl p-4 ${
                        reply.user_id === user.id
                          ? 'bg-primary/5 border border-primary/20'
                          : 'bg-green-50 border border-green-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          reply.user_id === user.id ? 'bg-primary' : 'bg-green-500'
                        }`}>
                          {reply.user_id === user.id ? (userProfile?.full_name || 'أ')[0] : 'د'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-bold">
                              {reply.user_id === user.id ? 'أنت' : 'فريق الدعم'}
                              {reply.user_id !== user.id && (
                                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded mr-2">
                                  دعم
                                </span>
                              )}
                            </p>
                            <span className="text-xs text-slate-500">
                              {new Date(reply.created_at).toLocaleString('ar-SA')}
                            </span>
                          </div>
                          <p className="mt-2 text-slate-700 whitespace-pre-wrap">{reply.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Input */}
                {selectedTicket.status !== 'closed' && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="اكتب ردك هنا..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                    />
                    <div className="flex justify-end mt-3">
                      <button
                        onClick={sendReply}
                        disabled={sendingReply || !replyText.trim()}
                        className="px-6 py-2 bg-primary text-white rounded-xl font-bold hover:bg-primary-dark flex items-center gap-2 disabled:opacity-50"
                      >
                        <Icon name={sendingReply ? "Loader" : "Send"} size={16} className={sendingReply ? "animate-spin" : ""} />
                        إرسال
                      </button>
                    </div>
                  </div>
                )}

                {selectedTicket.status === 'closed' && (
                  <div className="bg-slate-100 text-slate-600 text-center py-4 rounded-xl">
                    هذه التذكرة مغلقة. يمكنك إنشاء تذكرة جديدة إذا كنت بحاجة للمساعدة.
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                <Icon name="MessageSquare" size={48} className="text-slate-300 mx-auto mb-4" />
                <h3 className="font-bold text-lg text-slate-700 mb-2">اختر تذكرة</h3>
                <p className="text-slate-500">اختر تذكرة من القائمة لعرض التفاصيل والردود</p>
              </div>
            )}
          </div>
        </div>

        {/* New Ticket Modal */}
        {showNewTicketForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-xl">إنشاء تذكرة جديدة</h2>
                <button onClick={() => setShowNewTicketForm(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                  <Icon name="X" size={20} />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="block font-bold text-slate-900 mb-3">نوع المشكلة *</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {TICKET_CATEGORIES.map(category => (
                      <button
                        key={category.key}
                        onClick={() => setNewTicket(prev => ({ ...prev, category: category.key }))}
                        className={`p-4 rounded-xl text-center transition-all ${
                          newTicket.category === category.key
                            ? 'bg-primary/10 border-2 border-primary'
                            : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
                        }`}
                      >
                        <Icon name={category.icon} size={24} className={newTicket.category === category.key ? 'text-primary mx-auto' : 'text-slate-500 mx-auto'} />
                        <p className="font-bold text-sm mt-2">{category.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block font-bold text-slate-900 mb-2">الموضوع *</label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="ملخص قصير للمشكلة..."
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block font-bold text-slate-900 mb-2">الوصف التفصيلي *</label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="اشرح المشكلة بالتفصيل..."
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  />
                </div>

                {/* Priority */}
                <div>
                  <label className="block font-bold text-slate-900 mb-2">الأولوية</label>
                  <div className="flex gap-3">
                    {[
                      { key: 'low', label: 'منخفضة', color: 'slate' },
                      { key: 'medium', label: 'متوسطة', color: 'blue' },
                      { key: 'high', label: 'عالية', color: 'amber' },
                      { key: 'urgent', label: 'عاجلة', color: 'red' },
                    ].map(p => (
                      <button
                        key={p.key}
                        onClick={() => setNewTicket(prev => ({ ...prev, priority: p.key }))}
                        className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                          newTicket.priority === p.key
                            ? p.color === 'red' ? 'bg-red-500 text-white' :
                              p.color === 'amber' ? 'bg-amber-500 text-white' :
                              p.color === 'blue' ? 'bg-blue-500 text-white' :
                              'bg-slate-500 text-white'
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => setShowNewTicketForm(false)}
                  className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
                >
                  إلغاء
                </button>
                <button
                  onClick={createTicket}
                  disabled={creatingTicket || !newTicket.category || !newTicket.subject || !newTicket.description}
                  className="flex-1 px-6 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-dark flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Icon name={creatingTicket ? "Loader" : "Send"} size={18} className={creatingTicket ? "animate-spin" : ""} />
                  {creatingTicket ? 'جاري الإرسال...' : 'إرسال التذكرة'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
